import { useEffect, useRef, useState } from 'react';

// Reveal-on-scroll wrapper. The children are ALWAYS mounted (this is a reveal
// animation, not code-splitting), so the section keeps its height at all times.
// We only fade + slide the inner wrapper in once it enters the viewport.
//
// Important: the reveal must never leave content permanently invisible. A tall
// section combined with a large negative rootMargin can make the observer's
// intersection ratio never reach a high threshold, which previously left whole
// sections as blank gaps you scroll through. So we trigger on ANY intersection
// (threshold 0) and add a safety fallback that reveals no matter what.
function LazyOnView({ children, rootMargin = '0px 0px -10% 0px', threshold = 0, delay = 0 }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    // Respect users who don't want motion, and guard SSR / unsupported browsers.
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    let fallback;
    const reveal = () => {
      setAnimating(true);
      setIsVisible(true);
      clearTimeout(fallback);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (delay) setTimeout(reveal, delay);
          else reveal();
          observer.disconnect();
        }
      },
      // Force threshold 0: fire as soon as a single pixel crosses the
      // (margin-adjusted) root. Any positive ratio a caller passes could leave a
      // section taller than the shrunk root stuck below that ratio forever.
      { rootMargin, threshold: 0 }
    );

    if (ref.current) observer.observe(ref.current);

    // Safety net: never let content stay hidden. If the observer somehow never
    // fires (layout race, zero-height at mount, odd margins), reveal anyway.
    fallback = setTimeout(reveal, 1500 + delay);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, [delay, rootMargin, threshold]);

  return (
    <div ref={ref}>
      {/* Reveal on enter: fade + slide-up with an expo-out easing (premium feel).
          will-change is only set while animating, then dropped so we don't keep a
          compositor layer alive for every section (that caused scroll jank). */}
      <div
        onTransitionEnd={() => setAnimating(false)}
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'none' : 'translateY(28px)',
          transition:
            'opacity 700ms cubic-bezier(0.22, 1, 0.36, 1), transform 700ms cubic-bezier(0.22, 1, 0.36, 1)',
          willChange: animating ? 'opacity, transform' : 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default LazyOnView;
